import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  authAPI, 
  contactsAPI, 
  mapsAPI, 
  willStatusAPI, 
  childrenAPI,
  continuityAPI, 
  sharingAPI,
  dashboardAPI,
  setAuthToken,
  getStoredToken,
} from '../services/api';
import { MAP_TYPES as DEFAULT_MAP_TYPES } from '../store/mockData';

const LifelyContext = createContext(null);

export const useLifely = () => {
  const context = useContext(LifelyContext);
  if (!context) {
    throw new Error('useLifely must be used within a LifelyProvider');
  }
  return context;
};

export const LifelyProvider = ({ children }) => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Data state
  const [maps, setMaps] = useState(DEFAULT_MAP_TYPES);
  const [entries, setEntries] = useState({});
  const [mapNotes, setMapNotes] = useState({});
  const [mapWishes, setMapWishes] = useState({});
  const [contacts, setContacts] = useState([]);
  const [continuityConfig, setContinuityConfig] = useState(null);
  const [willStatus, setWillStatusState] = useState(null);
  const [childrenData, setChildrenData] = useState([]);
  const [shareSettings, setShareSettings] = useState([]);
  const [nominees, setNominees] = useState([]);
  
  // User's own contact preferences (alternative email/phone for check-ins)
  const [userContactPrefs, setUserContactPrefs] = useState({
    alternativeEmail: '',
    phone: '',
    useAlternativeEmail: false,
  });
  
  // Loading states
  const [loading, setLoading] = useState({
    maps: false,
    contacts: false,
    willStatus: false,
    continuity: false,
    children: false,
    sharing: false,
  });
  
  // Error state
  const [error, setError] = useState(null);

  // ============================================
  // Auth Operations
  // ============================================
  
  // Initialize auth from stored token
  useEffect(() => {
    const initAuth = async () => {
      const token = getStoredToken();
      if (token) {
        try {
          setAuthToken(token);
          const { user: userData } = await authAPI.me();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (err) {
          console.error('Auth init failed:', err);
          setAuthToken(null);
        }
      }
      setAuthLoading(false);
    };
    
    initAuth();
    
    // Listen for logout events
    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
      resetData();
    };
    
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Login with Memberstack token
  const login = useCallback(async (memberstackToken) => {
    try {
      setAuthLoading(true);
      setAuthToken(memberstackToken);
      const { user: userData } = await authAPI.login(memberstackToken);
      setUser(userData);
      setIsAuthenticated(true);
      return userData;
    } catch (err) {
      console.error('Login failed:', err);
      setAuthToken(null);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setAuthToken(null);
      setIsAuthenticated(false);
      setUser(null);
      resetData();
    }
  }, []);

  // Reset all data on logout
  const resetData = useCallback(() => {
    setMaps(DEFAULT_MAP_TYPES);
    setEntries({});
    setMapNotes({});
    setMapWishes({});
    setContacts([]);
    setContinuityConfig(null);
    setWillStatusState(null);
    setChildrenData([]);
    setShareSettings([]);
    setNominees([]);
  }, []);

  // ============================================
  // Data Fetching
  // ============================================

  // Fetch all maps
  const fetchMaps = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(prev => ({ ...prev, maps: true }));
    try {
      const { maps: mapsData } = await mapsAPI.list();
      
      // Merge API data with default map config
      const mergedMaps = DEFAULT_MAP_TYPES.map(defaultMap => {
        const apiMap = mapsData.find(m => m.type === defaultMap.id);
        return {
          ...defaultMap,
          apiId: apiMap?.id,
          notes: apiMap?.notes || '',
          entryCount: apiMap?.entry_count || 0,
          defaultWishesContactId: apiMap?.default_wishes_contact_id,
          defaultWishesContactName: apiMap?.default_wishes_contact_name,
          defaultWishesNotes: apiMap?.default_wishes_notes,
          status: apiMap?.entry_count > 0 ? 'configured' : 'not-configured',
        };
      });
      
      setMaps(mergedMaps);
      
      // Update map notes
      const notes = {};
      mergedMaps.forEach(m => {
        if (m.notes) notes[m.id] = m.notes;
      });
      setMapNotes(notes);
      
      // Update map wishes
      const wishes = {};
      mergedMaps.forEach(m => {
        if (m.defaultWishesContactId) {
          wishes[m.id] = {
            contactId: m.defaultWishesContactId,
            contactName: m.defaultWishesContactName,
            notes: m.defaultWishesNotes,
          };
        }
      });
      setMapWishes(wishes);
      
    } catch (err) {
      console.error('Failed to fetch maps:', err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, maps: false }));
    }
  }, [isAuthenticated]);

  // Fetch entries for a specific map
  const fetchEntriesForMap = useCallback(async (mapId) => {
    if (!isAuthenticated) return [];
    
    try {
      const { entries: entriesData } = await mapsAPI.listEntries(mapId);
      
      // Transform API response to match frontend format
      const transformedEntries = entriesData.map(entry => ({
        id: entry.id,
        label: entry.label,
        type: entry.type || 'not-specified',
        subtype: entry.subtype || 'not-specified',
        location: entry.location || '',
        priority: entry.priority || 'medium',
        notes: entry.notes || '',
        firstAction: entry.first_action || '',
        lastUpdated: entry.updated_at?.split('T')[0] || new Date().toISOString().split('T')[0],
        // Spread custom fields
        ...(entry.custom_fields || {}),
        // Wishes
        wishesContactId: entry.wishes_contact_id,
        wishesContactName: entry.wishes_contact_name,
        wishesNotes: entry.wishes_notes,
      }));
      
      setEntries(prev => ({ ...prev, [mapId]: transformedEntries }));
      return transformedEntries;
    } catch (err) {
      console.error(`Failed to fetch entries for ${mapId}:`, err);
      return [];
    }
  }, [isAuthenticated]);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(prev => ({ ...prev, contacts: true }));
    try {
      const { contacts: contactsData } = await contactsAPI.list();
      
      // Transform API response
      const transformedContacts = contactsData.map(c => ({
        id: c.id,
        name: c.name,
        category: c.category || 'other',
        role: c.role || '',
        email: c.email || '',
        backupEmail: c.backup_email || '',
        phone: c.phone || '',
        backupPhone: c.backup_phone || '',
        notes: c.notes || '',
        createdAt: c.created_at?.split('T')[0] || '',
      }));
      
      setContacts(transformedContacts);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, contacts: false }));
    }
  }, [isAuthenticated]);

  // Fetch will status
  const fetchWillStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(prev => ({ ...prev, willStatus: true }));
    try {
      const { willStatus: data } = await willStatusAPI.get();
      
      // Transform API response to frontend format
      setWillStatusState({
        willExists: data.will_exists || 'unsure',
        dateCreated: data.date_created || '',
        dateUpdated: data.date_updated || '',
        heldWith: data.held_with || '',
        firmName: data.firm_name || '',
        location: data.location || '',
        willHolder: data.will_holder || null,
        reminderDate: data.reminder_date || '',
        lastReviewedDate: data.last_reviewed_date || '',
        noChangesAtReview: !!data.no_changes_at_review,
        primaryContact: data.primary_contact || { name: '', role: '', email: '', phone: '' },
        executor1: data.executor1 || { name: '', relationship: '', email: '', phone: '' },
        executor2: data.executor2 || { name: '', relationship: '', email: '', phone: '' },
        executorNotes: data.executor_notes || '',
        scope: data.scope || {},
        poaExists: data.poa_exists || 'unsure',
        poaType: data.poa_type || '',
        poaOtherType: data.poa_other_type || '',
        poaActivation: data.poa_activation || '',
        poaHolders: [], // Fetched separately
        legalAdvisor: data.legal_advisor || { name: '', role: '', email: '', phone: '' },
        otherAdvisors: [], // Fetched separately
        lifelyReferenced: data.lifely_referenced || 'unsure',
        notes: data.notes || '',
        otherDocuments: [], // Fetched separately
      });
    } catch (err) {
      console.error('Failed to fetch will status:', err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, willStatus: false }));
    }
  }, [isAuthenticated]);

  // Fetch continuity config
  const fetchContinuity = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(prev => ({ ...prev, continuity: true }));
    try {
      const { continuity } = await continuityAPI.get();
      const { nominees: nomineesData } = await continuityAPI.nominees.list();
      
      setContinuityConfig({
        isConfigured: !!continuity.is_configured,
        isActive: !!continuity.is_active,
        isPaused: !!continuity.is_paused,
        pauseUntil: continuity.pause_until,
        checkInFrequency: continuity.check_in_frequency || 0,
        checkInInterval: continuity.check_in_frequency || 0,
        checkInUnit: continuity.check_in_unit || 'months',
        alternativeEmail: continuity.alternative_email || '',
        phoneNumber: continuity.phone_number || '',
        useEmail: !!continuity.use_email,
        useSms: !!continuity.use_sms,
        lastCheckIn: continuity.last_check_in,
        nextCheckIn: continuity.next_check_in,
        escalationConfig: continuity.escalation_config || [],
        nominees: nomineesData || [],
      });
      
      setNominees(nomineesData || []);
    } catch (err) {
      console.error('Failed to fetch continuity:', err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, continuity: false }));
    }
  }, [isAuthenticated]);

  // Fetch children
  const fetchChildren = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(prev => ({ ...prev, children: true }));
    try {
      const { children: data } = await childrenAPI.list();
      
      const transformedChildren = data.map(c => ({
        id: c.id,
        contactId: c.contact_id,
        name: c.name,
        dateOfBirth: c.date_of_birth || '',
        godparent1Id: c.godparent1_id,
        godparent1Name: c.godparent1_name || '',
        godparent2Id: c.godparent2_id,
        godparent2Name: c.godparent2_name || '',
        intendedGuardianId: c.intended_guardian_id,
        intendedGuardianName: c.intended_guardian_name || '',
        arrangementStatus: c.arrangement_status || 'none',
        specialNeeds: c.special_needs || '',
        notes: c.notes || '',
      }));
      
      setChildrenData(transformedChildren);
    } catch (err) {
      console.error('Failed to fetch children:', err);
    } finally {
      setLoading(prev => ({ ...prev, children: false }));
    }
  }, [isAuthenticated]);

  // Fetch sharing settings
  const fetchSharing = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(prev => ({ ...prev, sharing: true }));
    try {
      const { shareSettings: data } = await sharingAPI.list();
      // Transform snake_case from backend to camelCase for frontend
      const transformed = (data || []).map(s => ({
        id: s.id,
        contactId: s.contact_id,
        contactName: s.contact_name,  // Backend returns contact_name
        shareType: s.share_type,
        selectedMaps: s.selected_maps || [],
        includeWillStatus: !!s.include_will_status,
        includeContacts: !!s.include_contacts,
        shareTrigger: s.share_trigger,
        notes: s.notes,
      }));
      setShareSettings(transformed);
    } catch (err) {
      console.error('Failed to fetch sharing:', err);
    } finally {
      setLoading(prev => ({ ...prev, sharing: false }));
    }
  }, [isAuthenticated]);

  // Add share setting
  const addShareSetting = useCallback(async (shareData) => {
    if (!isAuthenticated) return;
    
    try {
      const { shareSetting: newShare } = await sharingAPI.create(shareData);
      // Transform backend response to frontend format
      const transformed = {
        id: newShare.id,
        contactId: newShare.contact_id,
        contactName: newShare.contact_name,
        shareType: newShare.share_type,
        selectedMaps: newShare.selected_maps || [],
        includeWillStatus: !!newShare.include_will_status,
        includeContacts: !!newShare.include_contacts,
        shareTrigger: newShare.share_trigger,
        notes: newShare.notes,
      };
      setShareSettings(prev => [...prev, transformed]);
      return transformed;
    } catch (err) {
      console.error('Failed to add share setting:', err);
      // Fallback to local state if API fails
      const localShare = { ...shareData, id: shareData.id || `share-${Date.now()}` };
      setShareSettings(prev => [...prev, localShare]);
      return localShare;
    }
  }, [isAuthenticated]);

  // Update share setting
  const updateShareSetting = useCallback(async (id, shareData) => {
    if (!isAuthenticated) return;
    
    try {
      await sharingAPI.update(id, shareData);
      setShareSettings(prev => prev.map(s => s.id === id ? { ...s, ...shareData } : s));
    } catch (err) {
      console.error('Failed to update share setting:', err);
      // Fallback to local state update
      setShareSettings(prev => prev.map(s => s.id === id ? { ...s, ...shareData } : s));
    }
  }, [isAuthenticated]);

  // Delete share setting
  const deleteShareSetting = useCallback(async (id) => {
    if (!isAuthenticated) return;
    
    try {
      await sharingAPI.delete(id);
      setShareSettings(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete share setting:', err);
      // Fallback to local state removal
      setShareSettings(prev => prev.filter(s => s.id !== id));
    }
  }, [isAuthenticated]);

  // ============================================
  // User Contact Preferences
  // ============================================

  // Get the user's full contact details (from Memberstack + preferences)
  const getUserContactDetails = useCallback(() => {
    if (!user) return null;
    return {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      primaryEmail: user.email || '',
      alternativeEmail: userContactPrefs.alternativeEmail || '',
      phone: userContactPrefs.phone || '',
      useAlternativeEmail: userContactPrefs.useAlternativeEmail || false,
      // The email to use for communications
      contactEmail: userContactPrefs.useAlternativeEmail && userContactPrefs.alternativeEmail 
        ? userContactPrefs.alternativeEmail 
        : user.email,
    };
  }, [user, userContactPrefs]);

  // Update user contact preferences
  const updateUserContactPrefs = useCallback(async (prefs) => {
    // Update local state immediately
    setUserContactPrefs(prev => ({ ...prev, ...prefs }));
    
    // TODO: Persist to backend when API is ready
    // For now, store in localStorage as backup
    try {
      const stored = JSON.parse(localStorage.getItem('lifely_user_prefs') || '{}');
      localStorage.setItem('lifely_user_prefs', JSON.stringify({ ...stored, contactPrefs: { ...userContactPrefs, ...prefs } }));
    } catch (err) {
      console.error('Failed to store user prefs:', err);
    }
  }, [userContactPrefs]);

  // Load user contact preferences from localStorage on auth
  useEffect(() => {
    if (isAuthenticated && user) {
      try {
        const stored = JSON.parse(localStorage.getItem('lifely_user_prefs') || '{}');
        if (stored.contactPrefs) {
          setUserContactPrefs(stored.contactPrefs);
        }
      } catch (err) {
        console.error('Failed to load user prefs:', err);
      }
    }
  }, [isAuthenticated, user]);

  // ============================================
  // Map Operations
  // ============================================

  const getMapById = useCallback((mapId) => {
    return maps.find(m => m.id === mapId);
  }, [maps]);

  const getEntriesForMap = useCallback((mapId) => {
    return entries[mapId] || [];
  }, [entries]);

  const getMapNotes = useCallback((mapId) => {
    return mapNotes[mapId] || '';
  }, [mapNotes]);

  const getMapWishes = useCallback((mapId) => {
    return mapWishes[mapId] || null;
  }, [mapWishes]);

  const updateMapNotes = useCallback(async (mapId, notes) => {
    if (!isAuthenticated) return;
    
    try {
      await mapsAPI.update(mapId, { notes });
      setMapNotes(prev => ({ ...prev, [mapId]: notes }));
    } catch (err) {
      console.error('Failed to update map notes:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const updateMapWishes = useCallback(async (mapId, wishes) => {
    if (!isAuthenticated) return;
    
    try {
      await mapsAPI.update(mapId, {
        defaultWishesContactId: wishes?.contactId || null,
        defaultWishesContactName: wishes?.contactName || null,
        defaultWishesNotes: wishes?.notes || null,
      });
      setMapWishes(prev => ({ ...prev, [mapId]: wishes }));
    } catch (err) {
      console.error('Failed to update map wishes:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const updateMapStatus = useCallback((mapId, status) => {
    setMaps(prev => prev.map(m => 
      m.id === mapId ? { ...m, status } : m
    ));
  }, []);

  // ============================================
  // Entry Operations
  // ============================================

  const addEntry = useCallback(async (mapId, entryData) => {
    if (!isAuthenticated) return null;
    
    try {
      // Extract custom fields
      const { label, type, subtype, location, priority, notes, firstAction, ...customFields } = entryData;
      
      const { entry } = await mapsAPI.createEntry(mapId, {
        label,
        type: type || 'not-specified',
        subtype: subtype || 'not-specified',
        location,
        priority: priority || 'medium',
        notes,
        firstAction,
        customFields,
      });
      
      const newEntry = {
        id: entry.id,
        label: entry.label,
        type: entry.type,
        subtype: entry.subtype,
        location: entry.location || '',
        priority: entry.priority,
        notes: entry.notes || '',
        firstAction: entry.first_action || '',
        lastUpdated: entry.updated_at?.split('T')[0] || new Date().toISOString().split('T')[0],
        ...(entry.custom_fields || {}),
      };
      
      setEntries(prev => ({
        ...prev,
        [mapId]: [...(prev[mapId] || []), newEntry],
      }));
      
      updateMapStatus(mapId, 'configured');
      return newEntry;
    } catch (err) {
      console.error('Failed to add entry:', err);
      throw err;
    }
  }, [isAuthenticated, updateMapStatus]);

  const updateEntry = useCallback(async (mapId, entryId, updates) => {
    if (!isAuthenticated) return;
    
    try {
      const { label, type, subtype, location, priority, notes, firstAction, ...customFields } = updates;
      
      const { entry } = await mapsAPI.updateEntry(mapId, entryId, {
        label,
        type,
        subtype,
        location,
        priority,
        notes,
        firstAction,
        customFields,
      });
      
      setEntries(prev => ({
        ...prev,
        [mapId]: prev[mapId].map(e => 
          e.id === entryId 
            ? {
                ...e,
                ...updates,
                lastUpdated: entry.updated_at?.split('T')[0] || new Date().toISOString().split('T')[0],
              }
            : e
        ),
      }));
    } catch (err) {
      console.error('Failed to update entry:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const deleteEntry = useCallback(async (mapId, entryId) => {
    if (!isAuthenticated) return;
    
    try {
      await mapsAPI.deleteEntry(mapId, entryId);
      setEntries(prev => ({
        ...prev,
        [mapId]: prev[mapId].filter(e => e.id !== entryId),
      }));
    } catch (err) {
      console.error('Failed to delete entry:', err);
      throw err;
    }
  }, [isAuthenticated]);

  // ============================================
  // Contact Operations
  // ============================================

  const addContact = useCallback(async (contactData) => {
    if (!isAuthenticated) return null;
    
    try {
      const { contact } = await contactsAPI.create(contactData);
      
      const newContact = {
        id: contact.id,
        name: contact.name,
        category: contact.category || 'other',
        role: contact.role || '',
        email: contact.email || '',
        backupEmail: contact.backup_email || '',
        phone: contact.phone || '',
        backupPhone: contact.backup_phone || '',
        notes: contact.notes || '',
        createdAt: contact.created_at?.split('T')[0] || '',
      };
      
      setContacts(prev => [...prev, newContact]);
      return newContact;
    } catch (err) {
      console.error('Failed to add contact:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const updateContact = useCallback(async (contactId, updates) => {
    if (!isAuthenticated) return;
    
    try {
      await contactsAPI.update(contactId, updates);
      setContacts(prev => prev.map(c => 
        c.id === contactId ? { ...c, ...updates } : c
      ));
    } catch (err) {
      console.error('Failed to update contact:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const removeContact = useCallback(async (contactId) => {
    if (!isAuthenticated) return;
    
    try {
      await contactsAPI.delete(contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (err) {
      console.error('Failed to remove contact:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const getContactById = useCallback((contactId) => {
    return contacts.find(c => c.id === contactId);
  }, [contacts]);

  const getContactsByCategory = useCallback((category) => {
    if (!category) return contacts;
    return contacts.filter(c => c.category === category);
  }, [contacts]);

  // ============================================
  // Will Status Operations
  // ============================================

  const updateWillStatus = useCallback(async (updates) => {
    if (!isAuthenticated) return;
    
    try {
      // Transform to API format
      const apiData = {
        willExists: updates.willExists,
        dateCreated: updates.dateCreated,
        dateUpdated: updates.dateUpdated,
        heldWith: updates.heldWith,
        firmName: updates.firmName,
        location: updates.location,
        willHolder: updates.willHolder,
        reminderDate: updates.reminderDate,
        lastReviewedDate: updates.lastReviewedDate,
        noChangesAtReview: updates.noChangesAtReview,
        primaryContact: updates.primaryContact,
        executor1: updates.executor1,
        executor2: updates.executor2,
        executorNotes: updates.executorNotes,
        scope: updates.scope,
        poaExists: updates.poaExists,
        poaType: updates.poaType,
        poaOtherType: updates.poaOtherType,
        poaActivation: updates.poaActivation,
        legalAdvisor: updates.legalAdvisor,
        lifelyReferenced: updates.lifelyReferenced,
        notes: updates.notes,
      };
      
      await willStatusAPI.update(apiData);
      setWillStatusState(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error('Failed to update will status:', err);
      throw err;
    }
  }, [isAuthenticated]);

  // ============================================
  // Continuity Operations
  // ============================================

  const updateContinuityConfig = useCallback(async (updates) => {
    if (!isAuthenticated) return;
    
    try {
      await continuityAPI.update(updates);
      setContinuityConfig(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error('Failed to update continuity config:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const performCheckIn = useCallback(async () => {
    if (!isAuthenticated) return null;
    
    try {
      const { lastCheckIn, nextCheckIn } = await continuityAPI.checkin();
      setContinuityConfig(prev => ({
        ...prev,
        lastCheckIn,
        nextCheckIn,
      }));
      return { lastCheckIn: new Date(lastCheckIn), nextCheckIn: new Date(nextCheckIn) };
    } catch (err) {
      console.error('Failed to check in:', err);
      throw err;
    }
  }, [isAuthenticated]);

  // Deactivate continuity (legacy - now uses save with isConfigured: false)
  const deactivateContinuity = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      await continuityAPI.deactivate();
      await fetchContinuity();
    } catch (err) {
      console.error('Failed to deactivate continuity:', err);
    }
  }, [isAuthenticated, fetchContinuity]);

  const saveContinuitySettings = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      await continuityAPI.activate();
      await fetchContinuity();
    } catch (err) {
      console.error('Failed to save continuity settings:', err);
      throw err;
    }
  }, [isAuthenticated, fetchContinuity]);

  const addNominee = useCallback(async (nomineeData) => {
    if (!isAuthenticated) return null;
    
    try {
      const { nominee } = await continuityAPI.nominees.create(nomineeData);
      setNominees(prev => [...prev, nominee]);
      setContinuityConfig(prev => ({
        ...prev,
        nominees: [...(prev?.nominees || []), nominee],
      }));
      return nominee;
    } catch (err) {
      console.error('Failed to add nominee:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const updateNominee = useCallback(async (nomineeId, updates) => {
    if (!isAuthenticated) return;
    
    try {
      await continuityAPI.nominees.update(nomineeId, updates);
      setNominees(prev => prev.map(n => 
        n.id === nomineeId ? { ...n, ...updates } : n
      ));
      setContinuityConfig(prev => ({
        ...prev,
        nominees: prev?.nominees?.map(n => 
          n.id === nomineeId ? { ...n, ...updates } : n
        ) || [],
      }));
    } catch (err) {
      console.error('Failed to update nominee:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const removeNominee = useCallback(async (nomineeId) => {
    if (!isAuthenticated) return;
    
    try {
      await continuityAPI.nominees.delete(nomineeId);
      setNominees(prev => prev.filter(n => n.id !== nomineeId));
      setContinuityConfig(prev => ({
        ...prev,
        nominees: prev?.nominees?.filter(n => n.id !== nomineeId) || [],
      }));
    } catch (err) {
      console.error('Failed to remove nominee:', err);
      throw err;
    }
  }, [isAuthenticated]);

  // ============================================
  // Children Operations
  // ============================================

  const addChild = useCallback(async (childData) => {
    if (!isAuthenticated) return null;
    
    try {
      const { child } = await childrenAPI.create(childData);
      const transformedChild = {
        id: child.id,
        contactId: child.contact_id,
        name: child.name,
        dateOfBirth: child.date_of_birth || '',
        godparent1Id: child.godparent1_id,
        godparent1Name: child.godparent1_name || '',
        godparent2Id: child.godparent2_id,
        godparent2Name: child.godparent2_name || '',
        intendedGuardianId: child.intended_guardian_id,
        intendedGuardianName: child.intended_guardian_name || '',
        arrangementStatus: child.arrangement_status || 'none',
        specialNeeds: child.special_needs || '',
        notes: child.notes || '',
      };
      setChildrenData(prev => [...prev, transformedChild]);
      return transformedChild;
    } catch (err) {
      console.error('Failed to add child:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const updateChild = useCallback(async (childId, updates) => {
    if (!isAuthenticated) return;
    
    try {
      await childrenAPI.update(childId, updates);
      setChildrenData(prev => prev.map(c => 
        c.id === childId ? { ...c, ...updates } : c
      ));
    } catch (err) {
      console.error('Failed to update child:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const removeChild = useCallback(async (childId) => {
    if (!isAuthenticated) return;
    
    try {
      await childrenAPI.delete(childId);
      setChildrenData(prev => prev.filter(c => c.id !== childId));
    } catch (err) {
      console.error('Failed to remove child:', err);
      throw err;
    }
  }, [isAuthenticated]);

  // ============================================
  // Summary Stats
  // ============================================

  const getSummaryStats = useCallback(() => {
    const totalMaps = maps.length;
    const configuredMaps = maps.filter(m => m.status === 'configured').length;
    const notConfiguredMaps = maps.filter(m => m.status === 'not-configured').length;
    const totalEntries = Object.values(entries).flat().length;
    
    const allEntries = Object.values(entries).flat();
    const criticalCount = allEntries.filter(e => e.priority === 'critical').length;
    const highCount = allEntries.filter(e => e.priority === 'high').length;
    
    const recentActivity = [];
    
    // Map entries activity
    Object.keys(entries).forEach(mapId => {
      const mapEntries = entries[mapId] || [];
      if (mapEntries.length > 0) {
        const map = maps.find(m => m.id === mapId);
        const mostRecentEntry = mapEntries.reduce((latest, entry) => {
          return !latest || entry.lastUpdated > latest.lastUpdated ? entry : latest;
        }, null);
        if (map && mostRecentEntry) {
          recentActivity.push({
            type: 'map',
            mapId,
            mapName: map.name,
            entryCount: mapEntries.length,
            lastUpdated: mostRecentEntry.lastUpdated,
            lastEntryLabel: mostRecentEntry.label,
            description: `Updated ${map.name}`,
          });
        }
      }
    });
    
    // Contact activity
    if (contacts && contacts.length > 0) {
      const sortedContacts = [...contacts].sort((a, b) => 
        (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || '')
      );
      const recentContact = sortedContacts[0];
      if (recentContact && (recentContact.updatedAt || recentContact.createdAt)) {
        recentActivity.push({
          type: 'contact',
          id: recentContact.id,
          name: recentContact.name,
          lastUpdated: recentContact.updatedAt || recentContact.createdAt,
          description: `Contact: ${recentContact.name}`,
          contactCount: contacts.length,
        });
      }
    }
    
    // Will status activity
    if (willStatus && willStatus.dateUpdated) {
      recentActivity.push({
        type: 'will',
        lastUpdated: willStatus.dateUpdated,
        description: 'Formal arrangements updated',
        willExists: willStatus.willExists,
      });
    }
    
    recentActivity.sort((a, b) => (b.lastUpdated || '').localeCompare(a.lastUpdated || ''));
    
    return {
      totalMaps,
      configuredMaps,
      notConfiguredMaps,
      totalEntries,
      criticalCount,
      highCount,
      recentActivity: recentActivity.slice(0, 5),
      continuityConfigured: continuityConfig?.isConfigured || false,
      nomineesCount: continuityConfig?.nominees?.length || 0,
      willExists: willStatus?.willExists || 'unsure',
      willHeldWith: willStatus?.heldWith || '',
      willFirmName: willStatus?.firmName || '',
      willReminderDate: willStatus?.reminderDate || null,
      totalContacts: contacts?.length || 0,
    };
  }, [maps, entries, continuityConfig, willStatus, contacts]);

  // Legacy compatibility for documents
  const addOtherDocument = useCallback((doc) => {
    const newDoc = { ...doc, id: `doc-${Date.now()}` };
    setWillStatusState(prev => ({
      ...prev,
      otherDocuments: [...(prev?.otherDocuments || []), newDoc],
    }));
    return newDoc;
  }, []);

  const removeOtherDocument = useCallback((docId) => {
    setWillStatusState(prev => ({
      ...prev,
      otherDocuments: (prev?.otherDocuments || []).filter(d => d.id !== docId),
    }));
  }, []);

  const addPoaHolder = useCallback((holder) => {
    const newHolder = { ...holder, id: `poa-holder-${Date.now()}` };
    setWillStatusState(prev => ({
      ...prev,
      poaHolders: [...(prev?.poaHolders || []), newHolder],
    }));
    return newHolder;
  }, []);

  const removePoaHolder = useCallback((holderId) => {
    setWillStatusState(prev => ({
      ...prev,
      poaHolders: (prev?.poaHolders || []).filter(h => h.id !== holderId),
    }));
  }, []);

  const addOtherAdvisor = useCallback((advisor) => {
    const newAdvisor = { ...advisor, id: `advisor-${Date.now()}` };
    setWillStatusState(prev => ({
      ...prev,
      otherAdvisors: [...(prev?.otherAdvisors || []), newAdvisor],
    }));
    return newAdvisor;
  }, []);

  const removeOtherAdvisor = useCallback((advisorId) => {
    setWillStatusState(prev => ({
      ...prev,
      otherAdvisors: (prev?.otherAdvisors || []).filter(a => a.id !== advisorId),
    }));
  }, []);

  // ============================================
  // Context Value
  // ============================================

  const value = {
    // Auth
    isAuthenticated,
    authLoading,
    user,
    login,
    logout,
    
    // Loading states
    loading,
    error,
    
    // Data fetching
    fetchMaps,
    fetchEntriesForMap,
    fetchContacts,
    fetchWillStatus,
    fetchContinuity,
    fetchChildren,
    fetchSharing,
    
    // Maps
    maps,
    entries,
    mapNotes,
    mapWishes,
    updateMapStatus,
    updateMapNotes,
    getMapById,
    getEntriesForMap,
    getMapNotes,
    getMapWishes,
    updateMapWishes,
    
    // Entries
    addEntry,
    updateEntry,
    deleteEntry,
    
    // Contacts
    contacts,
    addContact,
    updateContact,
    removeContact,
    getContactById,
    getContactsByCategory,
    
    // Will Status
    willStatus,
    updateWillStatus,
    addOtherDocument,
    removeOtherDocument,
    addPoaHolder,
    removePoaHolder,
    addOtherAdvisor,
    removeOtherAdvisor,
    
    // Children
    children: childrenData,
    addChild,
    updateChild,
    removeChild,
    
    // Continuity
    continuityConfig,
    updateContinuityConfig,
    addNominee,
    updateNominee,
    removeNominee,
    saveContinuitySettings,
    performCheckIn,
    deactivateContinuity,
    nominees,
    
    // Sharing
    shareSettings,
    addShareSetting,
    updateShareSetting,
    deleteShareSetting,
    
    // User Contact Preferences
    userContactPrefs,
    getUserContactDetails,
    updateUserContactPrefs,
    
    // Stats
    getSummaryStats,
  };

  return (
    <LifelyContext.Provider value={value}>
      {children}
    </LifelyContext.Provider>
  );
};

export default LifelyContext;
